import React from 'react';
import ReactDOM from 'react-dom';
import { XCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { message } from 'antd';
import { updateIncludeChecklist, updateAllIncludeChecklist } from '../../../api/mastersheetApi';
import { normalizeQuarter } from '../utils/helpers';

const PresetRequirementsModal = ({ 
  show, 
  onClose, 
  presetRequirements, 
  setPresetRequirements,
  selectedStakeholders,
  setSelectedStakeholders,
  turnAllOff,
  setTurnAllOff
}) => {
  if (!show) return null;

  const handleStakeholderClick = (stakeholder) => {
    setSelectedStakeholders(prev => 
      prev.includes(stakeholder) 
        ? prev.filter(s => s !== stakeholder)
        : [...prev, stakeholder]
    );
  };

  const handleTurnAllToggle = async () => {
    try {
      const userId = localStorage.getItem('userid');
      const newValue = turnAllOff ? "Yes" : "No";  // If currently "Off", we're turning all "On" and vice versa
      
      // Update local state immediately for responsive UI
      setTurnAllOff(!turnAllOff);
      
      // Update all requirements in local state
      setPresetRequirements(prev =>
        prev.map(item => ({
          ...item,
          include_checklist: newValue
        }))
      );

      // Call API to update all requirements
      const updatedRequirements = await updateAllIncludeChecklist(userId, newValue);
      
      // Update local state with server response
      if (Array.isArray(updatedRequirements)) {
        setPresetRequirements(updatedRequirements);
      }

      message.success(`All requirements turned ${newValue}`);
    } catch (error) {
      console.error('Error updating all requirements:', error);
      message.error('Failed to update all requirements');
      
      // Revert local state if API call fails
      setTurnAllOff(turnAllOff);
      setPresetRequirements(prev =>
        prev.map(item => ({
          ...item,
          include_checklist: turnAllOff ? "No" : "Yes"
        }))
      );
    }
  };

  const handleToggleRequirement = async (req, isChecked) => {
    try {
      const newValue = isChecked ? "Yes" : "No";
      
      // Update local state immediately for responsive UI
      setPresetRequirements(prev =>
        prev.map(item =>
          item.unid === req.unid
            ? { ...item, include_checklist: newValue }
            : item
        )
      );

      // Get userId from localStorage
      const userId = localStorage.getItem('userid');
      
      // Call the API to update the database
      const updates = {
        [req.unid]: newValue
      };
      
      const updatedRequirements = await updateIncludeChecklist(userId, updates);
      
      // Update the local state with the response from the server
      if (Array.isArray(updatedRequirements)) {
        const updatedReq = updatedRequirements.find(r => r.unid === req.unid);
        if (updatedReq) {
          setPresetRequirements(prev =>
            prev.map(item =>
              item.unid === req.unid
                ? { ...item, include_checklist: updatedReq.include_checklist }
                : item
            )
          );
        }
      }

      // Show success message
      message.success('Requirement updated successfully');

    } catch (error) {
      console.error('Error updating requirement:', error);
      message.error('Failed to update requirement');
      
      // Revert local state if API call fails
      setPresetRequirements(prev =>
        prev.map(item =>
          item.unid === req.unid
            ? { ...item, include_checklist: req.include_checklist }
            : item
        )
      );
    }
  };

  return ReactDOM.createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        className="fixed inset-0 bg-black bg-opacity-50 z-[99] flex items-center justify-center"
      >
        <motion.div
          initial={{ y: 20 }}
          animate={{ y: 0 }}
          exit={{ y: 20 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="bg-white rounded-lg p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto"
        >
          {/* Modal header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-semibold">401k Requirements Dashboard</h2>
            <div className="flex items-center gap-4">
              <button
                onClick={handleTurnAllToggle}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-full text-sm transition-colors"
              >
                <span>Turn All {turnAllOff ? 'On' : 'Off'}</span>
              </button>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
              >
                <XCircle size={20} />
              </button>
            </div>
          </div>

          {/* Stakeholder Filter Buttons */}
          <div className="flex gap-3 mb-8">
            <button
              onClick={() => handleStakeholderClick('Plan Sponsor')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors
                ${selectedStakeholders.includes('Plan Sponsor')
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700'}`}
            >
              Plan Sponsor
            </button>
            <button
              onClick={() => handleStakeholderClick('TPA')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors
                ${selectedStakeholders.includes('TPA')
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700'}`}
            >
              TPA
            </button>
            <button
              onClick={() => handleStakeholderClick('Recordkeeper')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors
                ${selectedStakeholders.includes('Recordkeeper')
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700'}`}
            >
              Recordkeeper
            </button>
          </div>

          {/* Requirements List */}
          <div className="space-y-8">
            {['1', '2', '3', '4'].map(quarter => {
              const quarterRequirements = (presetRequirements || [])
                .filter(req => {
                  const normalizedReqQuarter = normalizeQuarter(req.quarter);
                  const normalizedQuarter = normalizeQuarter(quarter);
                  return normalizedReqQuarter === normalizedQuarter && 
                         selectedStakeholders.includes(req.stakeholders);
                });
              
              // Only render quarter section if it has requirements
              if (quarterRequirements.length === 0) return null;

              return (
                <div key={quarter} className="space-y-4">
                  <h3 className="font-medium text-xl">Q{quarter}</h3>
                  <div className="space-y-3">
                    {quarterRequirements.map((req) => (
                      <div 
                        key={req.unid} 
                        className="bg-gray-50 rounded-lg p-4 shadow-sm"
                      >
                        <div className="flex items-start gap-4">
                          <span className="text-sm text-gray-700 flex-1 pr-4 leading-normal">
                            {req.requirement}
                          </span>
                          <div className="relative inline-block w-12 h-6 flex-shrink-0">
                            <input
                              type="checkbox"
                              id={req.unid}
                              checked={turnAllOff ? false : (req.include_checklist === "Yes")}
                              onChange={(e) => handleToggleRequirement(req, e.target.checked)}
                              className="peer sr-only"
                            />
                            <label
                              htmlFor={req.unid}
                              className={`
                                absolute cursor-pointer inset-0 rounded-full
                                ${req.include_checklist === "Yes" ? 'bg-blue-500' : 'bg-gray-300'}
                                transition-colors duration-200
                              `}
                            >
                              <span
                                className={`
                                  absolute left-1 top-1 bg-white w-4 h-4 rounded-full 
                                  transition-transform duration-200 transform
                                  ${req.include_checklist === "Yes" ? 'translate-x-6' : 'translate-x-0'}
                                `}
                              />
                            </label>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default PresetRequirementsModal;